/* eslint-disable @typescript-eslint/no-explicit-any */
import { BLOCKS } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import themeGet from '@styled-system/theme-get';
import styled from 'styled-components';

import { filterContentfulArray } from '../utils/regionLockedContent';
import { useProvince } from '../state/ProvinceProvider';

import ProductLink from './ProductLink';
import RichText from './RichTextV2';
import SectionContainer from './SectionContainer';
import { Body2, Heading2, Heading4Css } from './Typography';
import GetAQuoteButtonOrLink from './GetAQuoteRenewalCTA';
import { HeaderCallToAction } from './styles';

const options: Options = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_3]: (_, children) => <SubHeading>{children}</SubHeading>,
  },
};

const bulletOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Body>{children}</Body>,
  },
};

interface HowItWorksProductsProps {
  data: any;
  howItWorksSection: any;
}

const HowItWorksProducts = ({ data, howItWorksSection }: HowItWorksProductsProps) => {
  const { sectionId, links } = data;
  const { activeProvince } = useProvince();
  const filteredByRegionLinks = filterContentfulArray(activeProvince, links);
  const { sectionTitle, combinatorSectionItems, linkedResources } = howItWorksSection;

  const iconCards: any[] = combinatorSectionItems.filter(
    (item: { internal?: { type: string }}) => item?.internal?.type === 'ContentfulIconCard',
  );

  return (
    <Section id={sectionId}>
      <SectionContainer>
        <ProductBody>
          <Wrapper>
            {filteredByRegionLinks.map((link: any) => <Product key={link.id} variant="secondary" link={link} />)}
          </Wrapper>
          <RichText document={sectionTitle} options={options} />
          <Steps>
            {iconCards.map((item, index) => (
              <Step key={item.id}>
                <StepNumber>
                  <StepNumberHeading>
                    {index + 1}
                  </StepNumberHeading>
                </StepNumber>
                <SubHeading>{item.heading}</SubHeading>
                {item.paragraphRTF && (
                  <div>
                    <RichText
                      document={item.paragraphRTF}
                      options={bulletOptions}
                    />
                  </div>
                )}
              </Step>
            ))}
          </Steps>
          {linkedResources && (
            <GetAQuoteButtonOrLink
              cta={linkedResources}
              CtaComponent={HeaderCallToAction}
              inline
              phoneNumberColor="red"
            />
          )}
        </ProductBody>
      </SectionContainer>
    </Section>
  );
};

const StepNumber = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  padding: 6px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  background-color: ${themeGet('colors.stone')};
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 32px;
  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
    gap: 32px;
  }
`;
const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;
  ul {
    text-align: center;
  }
  margin-bottom: 32px;
`;

const SubHeading = styled.h3`
  text-align: center;
  ${Heading4Css}
`;

const StepNumberHeading = styled.h3`
  ${Heading4Css}
`;

const Heading = styled(Heading2)`
  text-align: center;
  margin: 60px 20px 10px;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 60px 0 40px;
  }
`;

const Body = styled(Body2)`
  margin-bottom: 0px;
  text-align: center;
`;

const Section = styled.section`
  margin-top: 100px;
  background-color: ${themeGet('colors.white')};
  margin-bottom: 40px;
`;

const ProductBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: solid 1px ${themeGet('colors.stone')};
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  padding: 40px 10px 40px;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding: 40px 0px 60px;
  }
`;

const Wrapper = styled.div`
  display: inline-flex;
  align-items: flex-start;
  justify-content: space-around;
  flex-wrap: wrap;
  max-width: 520px;
  margin-top: -115px;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    max-width: 100%;
    padding: 0 10px;
    justify-content: center;
    flex-wrap: nowrap;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 0 20px;
  }
`;

const Product = styled(ProductLink)`
  max-width: 134px;
  margin: 10px;

  @media only screen and (min-width: ${themeGet('breakpoints.sm')}) {
    margin: 20px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 0px 8px;
    max-width: 184px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin: 0px 12px;
    p {
      font-size: ${themeGet('typography.desktop.body2.fontSize')};
      line-height: ${themeGet('typography.desktop.body2.lineHeight')};
    }
  }
`;

export default HowItWorksProducts;
